import apiClient, { generateUUID } from './apiClient';

// ==========================================
// 1. AUTH & IDENTITY SERVICES
// ==========================================
export const authService = {
  login: async (userName, password) => {
    const response = await apiClient.post('/api/v1/auth/login', {
      userName,
      password,
    });
    return response.data;
  },

  getMe: async () => {
    const response = await apiClient.get('/api/v1/identity/me');
    return response.data;
  },

  changePassword: async (oldPassword, newPassword) => {
    const response = await apiClient.post('/api/v1/auth/change-password', {
      oldPassword,
      newPassword,
    });
    return response.data;
  },

  logout: async () => {
    const refreshToken = localStorage.getItem('fastline_refresh_token');
    try {
      if (refreshToken) {
        await apiClient.post('/api/v1/auth/revoke', { refreshToken });
      }
    } catch (e) {
      console.warn('Logout revoke warning:', e);
    } finally {
      localStorage.removeItem('fastline_access_token');
      localStorage.removeItem('fastline_refresh_token');
      localStorage.removeItem('fastline_user');
      localStorage.removeItem('fastline_company');
    }
  },
};

// ==========================================
// 2. COMPANY PROFILE & SETTINGS SERVICES
// ==========================================
export const companyService = {
  getCompany: async () => {
    const response = await apiClient.get('/api/v1/company');
    return response.data;
  },

  updateCompany: async (data) => {
    const response = await apiClient.put('/api/v1/company', data);
    return response.data;
  },

  getSettings: async () => {
    const response = await apiClient.get('/api/v1/company/settings');
    return response.data;
  },

  updateSettings: async (settings) => {
    const response = await apiClient.put('/api/v1/company/settings', settings);
    return response.data;
  },

  getBranches: async (page = 1, pageSize = 25) => {
    const response = await apiClient.get(`/api/v1/company/branches?page=${page}&pageSize=${pageSize}`);
    return response.data;
  },

  createBranch: async (branchData) => {
    const response = await apiClient.post('/api/v1/company/branches', branchData);
    return response.data;
  },

  updateBranch: async (branchId, branchData) => {
    const response = await apiClient.put(`/api/v1/company/branches/${branchId}`, branchData);
    return response.data;
  },

  // Company Users
  getCompanyUsers: async (page = 1, pageSize = 50) => {
    const response = await apiClient.get(`/api/v1/company/users?page=${page}&pageSize=${pageSize}`);
    return response.data;
  },

  getCompanyUserById: async (id) => {
    const response = await apiClient.get(`/api/v1/company/users/${id}`);
    return response.data;
  },

  createCompanyUser: async (userData) => {
    const response = await apiClient.post('/api/v1/company/users', userData);
    return response.data;
  },

  updateCompanyUser: async (id, userData) => {
    const response = await apiClient.put(`/api/v1/company/users/${id}`, userData);
    return response.data;
  },

  updateCompanyUserPassword: async (id, newPassword) => {
    const response = await apiClient.put(`/api/v1/company/users/${id}/password`, { newPassword });
    return response.data;
  },

  updateCompanyUserStatus: async (id, isActive) => {
    const response = await apiClient.put(`/api/v1/company/users/${id}/status`, { isActive });
    return response.data;
  },

  // Roles
  getRestaurantRoles: async () => {
    const response = await apiClient.get('/api/v1/company/restaurant-roles');
    return response.data;
  },

  getCompanyRoles: async () => {
    const response = await apiClient.get('/api/v1/company/roles');
    return response.data;
  },
};

// ==========================================
// 3. ORDERS & DISPATCH SERVICES
// ==========================================
export const ordersService = {
  getOrders: async (params = {}) => {
    const response = await apiClient.get('/api/v1/orders', { params });
    return response.data;
  },

  getOrderById: async (orderId) => {
    const response = await apiClient.get(`/api/v1/orders/${orderId}`);
    return response.data;
  },

  createOrder: async (orderData) => {
    const response = await apiClient.post('/api/v1/orders', orderData);
    return response.data;
  },

  startParallelDispatch: async (orderId, dispatchConfig = {}) => {
    const response = await apiClient.post(`/api/v1/orders/${orderId}/dispatch/parallel`, dispatchConfig);
    return response.data;
  },

  assignCourierManually: async (orderId, courierId) => {
    const response = await apiClient.post(`/api/v1/orders/${orderId}/assign-courier`, { courierId });
    return response.data;
  },

  cancelOrder: async (orderId, reason, stage = 'PreDispatch') => {
    const response = await apiClient.post(`/api/v1/orders/${orderId}/cancel`, {
      reason,
      cancellationStage: stage,
    });
    return response.data;
  },
};

// ==========================================
// 4. COURIERS, VEHICLES & DOCUMENTS SERVICES
// ==========================================
export const couriersService = {
  // Couriers
  getCouriers: async (params = {}) => {
    const response = await apiClient.get('/api/v1/couriers', { params });
    return response.data;
  },

  getCourierById: async (courierId) => {
    const response = await apiClient.get(`/api/v1/couriers/${courierId}`);
    return response.data;
  },

  createCourier: async (courierData) => {
    const response = await apiClient.post('/api/v1/couriers', courierData);
    return response.data;
  },

  updateCourier: async (courierId, data) => {
    const response = await apiClient.put(`/api/v1/couriers/${courierId}`, data);
    return response.data;
  },

  // Vehicles
  getAllVehicles: async () => {
    try {
      const response = await apiClient.get('/api/v1/couriers/vehicles');
      return response.data;
    } catch (e) {
      // Aggregate across couriers if global list is not directly supported
      try {
        const couriersRes = await apiClient.get('/api/v1/couriers');
        const list = Array.isArray(couriersRes.data) ? couriersRes.data : (couriersRes.data?.items || []);
        const allVehicles = [];
        for (const c of list) {
          try {
            const cId = c.courierId ?? c.id;
            const vehs = await apiClient.get(`/api/v1/couriers/${cId}/vehicles`);
            const vItems = Array.isArray(vehs.data) ? vehs.data : (vehs.data?.items || []);
            vItems.forEach((v) => allVehicles.push({
              ...v,
              courierId: cId,
              courierName: c.fullNameAr || c.name || c.fullName,
              courierPhone: c.mobile || c.phone,
            }));
          } catch (err) {}
        }
        return allVehicles;
      } catch (err) {
        return [];
      }
    }
  },

  getCourierVehicles: async (courierId) => {
    const response = await apiClient.get(`/api/v1/couriers/${courierId}/vehicles`);
    return response.data;
  },

  createVehicle: async (vehicleData) => {
    const payload = {
      courierId: Number(vehicleData.courierId),
      vehicleType: typeof vehicleData.vehicleType === 'number' ? vehicleData.vehicleType : 1,
      plateNumber: vehicleData.plateNumber?.trim(),
      capacityOrders: Number(vehicleData.capacityOrders) || 3,
      licenseExpiresOn: vehicleData.licenseExpiresOn || new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      isPrimary: vehicleData.isPrimary !== undefined ? vehicleData.isPrimary : true,
      isActive: vehicleData.isActive !== undefined ? vehicleData.isActive : true,
    };
    const response = await apiClient.post('/api/v1/couriers/vehicles', payload);
    return response.data;
  },

  updateVehicle: async (vehicleId, vehicleData) => {
    const payload = {
      vehicleType: vehicleData.vehicleType !== undefined ? vehicleData.vehicleType : null,
      plateNumber: vehicleData.plateNumber,
      model: vehicleData.model,
      color: vehicleData.color,
    };
    const response = await apiClient.put(`/api/v1/couriers/vehicles/${vehicleId}`, payload);
    return response.data;
  },

  // Document Types
  getDocumentTypes: async () => {
    const response = await apiClient.get('/api/v1/couriers/document-types');
    return response.data;
  },

  createDocumentType: async (data) => {
    const response = await apiClient.post('/api/v1/couriers/document-types', data);
    return response.data;
  },

  updateDocumentType: async (typeId, data) => {
    const response = await apiClient.put(`/api/v1/couriers/document-types/${typeId}`, data);
    return response.data;
  },

  // Documents
  getCourierDocuments: async (courierId) => {
    const response = await apiClient.get(`/api/v1/couriers/${courierId}/documents`);
    return response.data;
  },

  createDocument: async (docData) => {
    const isFormData = docData instanceof FormData;
    const response = await apiClient.post('/api/v1/couriers/documents', docData, {
      headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : {},
    });
    return response.data;
  },

  updateDocument: async (docId, docData) => {
    const isFormData = docData instanceof FormData;
    const response = await apiClient.put(`/api/v1/couriers/documents/${docId}`, docData, {
      headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : {},
    });
    return response.data;
  },

  verifyDocument: async (docId, { isApproved, notes = '' }) => {
    const response = await apiClient.post(`/api/v1/couriers/documents/${docId}/verify`, {
      isApproved,
      notes,
    });
    return response.data;
  },

  // Legacy review alias
  reviewDocument: async (courierId, docId, isApproved, notes = '') => {
    const response = await apiClient.post(`/api/v1/couriers/documents/${docId}/verify`, {
      isApproved,
      notes,
    });
    return response.data;
  },

  // Courier App User Account Management
  getCourierUser: async (courierId) => {
    const response = await apiClient.get(`/api/v1/couriers/${courierId}/user`);
    return response.data;
  },

  createCourierUser: async (courierId, userData) => {
    const response = await apiClient.post(`/api/v1/couriers/${courierId}/user`, userData);
    return response.data;
  },

  updateCourierUser: async (courierId, userData) => {
    const response = await apiClient.put(`/api/v1/couriers/${courierId}/user`, userData);
    return response.data;
  },

  updateCourierUserPassword: async (courierId, newPassword) => {
    const response = await apiClient.put(`/api/v1/couriers/${courierId}/user/password`, { newPassword });
    return response.data;
  },

  updateCourierUserStatus: async (courierId, isActive) => {
    const response = await apiClient.put(`/api/v1/couriers/${courierId}/user/status`, { isActive });
    return response.data;
  },
};

// ==========================================
// 5. RESTAURANTS & RESTAURANT USERS SERVICES
// ==========================================
export const restaurantsService = {
  getRestaurants: async (params = {}) => {
    const response = await apiClient.get('/api/v1/restaurants', { params });
    return response.data;
  },

  getRestaurantById: async (restaurantId) => {
    const response = await apiClient.get(`/api/v1/restaurants/${restaurantId}`);
    return response.data;
  },

  createRestaurant: async (data) => {
    const response = await apiClient.post('/api/v1/restaurants', data);
    return response.data;
  },

  updateRestaurant: async (restaurantId, data) => {
    const response = await apiClient.put(`/api/v1/restaurants/${restaurantId}`, data);
    return response.data;
  },


  // Branches
  getRestaurantBranches: async (restaurantId) => {
    const url = restaurantId ? `/api/v1/restaurants/branches?restaurantId=${restaurantId}` : '/api/v1/restaurants/branches';
    const response = await apiClient.get(url);
    return response.data;
  },

  createRestaurantBranch: async (restaurantId, branchData) => {
    const payload = {
      restaurantId: Number(restaurantId || branchData.restaurantId),
      branchCode: branchData.branchCode,
      branchNameAr: branchData.branchNameAr,
      branchNameEn: branchData.branchNameEn || branchData.branchNameAr,
      latitude: parseFloat(branchData.latitude) || 30.0444,
      longitude: parseFloat(branchData.longitude) || 31.2357,
      addressText: branchData.addressText || '',
      phone: branchData.phone || '',
      maximumBatchOrders: Number(branchData.maximumBatchOrders) || 1,
      isBatchingEnabled: branchData.isBatchingEnabled !== undefined ? branchData.isBatchingEnabled : true,
      isActive: branchData.isActive !== undefined ? branchData.isActive : true,
      rowVersion: branchData.rowVersion,
    };
    const response = await apiClient.post('/api/v1/restaurants/branches', payload);
    return response.data;
  },

  updateRestaurantBranch: async (id, branchData) => {
    const payload = {
      restaurantId: Number(branchData.restaurantId),
      branchCode: branchData.branchCode,
      branchNameAr: branchData.branchNameAr,
      branchNameEn: branchData.branchNameEn || branchData.branchNameAr,
      latitude: parseFloat(branchData.latitude) || 30.0444,
      longitude: parseFloat(branchData.longitude) || 31.2357,
      addressText: branchData.addressText || '',
      phone: branchData.phone || '',
      maximumBatchOrders: Number(branchData.maximumBatchOrders) || 1,
      isBatchingEnabled: branchData.isBatchingEnabled !== undefined ? branchData.isBatchingEnabled : true,
      isActive: branchData.isActive !== undefined ? branchData.isActive : true,
      rowVersion: branchData.rowVersion,
    };
    const response = await apiClient.put(`/api/v1/restaurants/branches/${id}`, payload);
    return response.data;
  },

  // Contracts
  getContracts: async (restaurantId) => {
    const response = await apiClient.get(`/api/v1/restaurants/${restaurantId}/contracts`);
    return response.data;
  },

  createContract: async (contractData) => {
    const payload = {
      restaurantId: Number(contractData.restaurantId),
      contractNumber: contractData.contractNumber,
      effectiveFrom: contractData.effectiveFrom || new Date().toISOString().split('T')[0],
      effectiveTo: contractData.effectiveTo || new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      courierPaymentDisplayMode: Number(contractData.courierPaymentDisplayMode) || 0,
      additionalOrderFeePercentage: parseFloat(contractData.additionalOrderFeePercentage) || 0,
      allowZeroDeliveryFee: typeof contractData.allowZeroDeliveryFee === 'boolean' ? contractData.allowZeroDeliveryFee : Boolean(contractData.allowZeroDeliveryFee),
      statementCycle: Number(contractData.statementCycle) || 0,
      paymentTermDays: Number(contractData.paymentTermDays) || 0,
      status: Number(contractData.status) !== undefined ? Number(contractData.status) : 0,
      branchIds: Array.isArray(contractData.branchIds) ? contractData.branchIds.map(Number) : [],
      rowVersion: contractData.rowVersion || '',
    };
    const response = await apiClient.post('/api/v1/restaurants/contracts', payload);
    return response.data;
  },

  updateContract: async (id, contractData) => {
    const payload = {
      restaurantId: Number(contractData.restaurantId),
      contractNumber: contractData.contractNumber,
      effectiveFrom: contractData.effectiveFrom,
      effectiveTo: contractData.effectiveTo,
      courierPaymentDisplayMode: Number(contractData.courierPaymentDisplayMode) || 0,
      additionalOrderFeePercentage: parseFloat(contractData.additionalOrderFeePercentage) || 0,
      allowZeroDeliveryFee: typeof contractData.allowZeroDeliveryFee === 'boolean' ? contractData.allowZeroDeliveryFee : Boolean(contractData.allowZeroDeliveryFee),
      statementCycle: Number(contractData.statementCycle) || 0,
      paymentTermDays: Number(contractData.paymentTermDays) || 0,
      status: Number(contractData.status) !== undefined ? Number(contractData.status) : 0,
      branchIds: Array.isArray(contractData.branchIds) ? contractData.branchIds.map(Number) : [],
      rowVersion: contractData.rowVersion || '',
    };
    const response = await apiClient.put(`/api/v1/restaurants/contracts/${id}`, payload);
    return response.data;
  },

  // Restaurant Portal Users
  getRestaurantUsers: async (restaurantId) => {
    if (!restaurantId || restaurantId === 'undefined') return [];
    const response = await apiClient.get(`/api/v1/restaurants/${restaurantId}/users`);
    return response.data;
  },

  getRestaurantUserById: async (restaurantId, userId) => {
    if (!restaurantId || !userId) return null;
    const response = await apiClient.get(`/api/v1/restaurants/${restaurantId}/users/${userId}`);
    return response.data;
  },

  createRestaurantUser: async (restaurantId, userData) => {
    if (!restaurantId) throw new Error('Restaurant ID is required');
    const payload = {
      restaurantBranchId: userData.restaurantBranchId !== undefined && userData.restaurantBranchId !== '' ? Number(userData.restaurantBranchId) : 0,
      userName: userData.userName,
      fullName: userData.fullName || userData.userName,
      email: userData.email || '',
      mobile: userData.mobile || userData.phone || '',
      isActive: userData.isActive !== undefined ? userData.isActive : true,
      password: userData.password,
      roleIds: userData.roleIds || [1],
      rowVersion: userData.rowVersion || '',
    };
    const response = await apiClient.post(`/api/v1/restaurants/${restaurantId}/users`, payload);
    return response.data;
  },

  updateRestaurantUser: async (restaurantId, userId, userData) => {
    if (!restaurantId || !userId) throw new Error('Restaurant ID and User ID are required');
    const payload = {
      restaurantBranchId: userData.restaurantBranchId !== undefined && userData.restaurantBranchId !== '' ? Number(userData.restaurantBranchId) : 0,
      userName: userData.userName,
      fullName: userData.fullName,
      email: userData.email,
      mobile: userData.mobile || userData.phone,
      isActive: userData.isActive !== undefined ? userData.isActive : true,
      role: userData.role,
      rowVersion: userData.rowVersion || '',
    };
    const response = await apiClient.put(`/api/v1/restaurants/${restaurantId}/users/${userId}`, payload);
    return response.data;
  },

  updateRestaurantUserPassword: async (restaurantId, userId, newPassword) => {
    if (!restaurantId || !userId) throw new Error('Restaurant ID and User ID are required');
    const response = await apiClient.put(`/api/v1/restaurants/${restaurantId}/users/${userId}/password`, { newPassword });
    return response.data;
  },

  updateRestaurantUserStatus: async (restaurantId, userId, isActive) => {
    if (!restaurantId || !userId) throw new Error('Restaurant ID and User ID are required');
    const response = await apiClient.put(`/api/v1/restaurants/${restaurantId}/users/${userId}/status`, { isActive });
    return response.data;
  },

  // Pricing Plans & Rules
  getPricingPlans: async () => {
    const response = await apiClient.get('/api/v1/restaurants/pricing-plans');
    return response.data;
  },

  createPricingPlan: async (planData) => {
    const payload = {
      planCode: planData.planCode,
      planNameAr: planData.planNameAr,
      effectiveFrom: planData.effectiveFrom || new Date().toISOString().split('T')[0],
      effectiveTo: planData.effectiveTo || new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      additionalOrderFeePercentage: parseFloat(planData.additionalOrderFeePercentage) || 0,
      isActive: planData.isActive !== undefined ? planData.isActive : true,
      rowVersion: planData.rowVersion,
    };
    const response = await apiClient.post('/api/v1/restaurants/pricing-plans', payload);
    return response.data;
  },

  updatePricingPlan: async (id, planData) => {
    const payload = {
      planCode: planData.planCode,
      planNameAr: planData.planNameAr,
      effectiveFrom: planData.effectiveFrom,
      effectiveTo: planData.effectiveTo,
      additionalOrderFeePercentage: parseFloat(planData.additionalOrderFeePercentage) || 0,
      isActive: planData.isActive !== undefined ? planData.isActive : true,
      rowVersion: planData.rowVersion,
    };
    const response = await apiClient.put(`/api/v1/restaurants/pricing-plans/${id}`, payload);
    return response.data;
  },

  // Pricing Rules
  getPricingRules: async (planId) => {
    const url = planId ? `/api/v1/restaurants/pricing-rules?planId=${planId}` : '/api/v1/restaurants/pricing-rules';
    const response = await apiClient.get(url);
    return response.data;
  },

  createPricingRule: async (ruleData) => {
    const payload = {
      restaurantPricingPlanId: Number(ruleData.restaurantPricingPlanId),
      restaurantBranchId: ruleData.restaurantBranchId ? Number(ruleData.restaurantBranchId) : null,
      serviceZoneId: ruleData.serviceZoneId ? Number(ruleData.serviceZoneId) : null,
      calculationType: Number(ruleData.calculationType) || 1,
      fixedFee: parseFloat(ruleData.fixedFee) || 0,
      baseFee: parseFloat(ruleData.baseFee) || 0,
      includedDistanceKm: parseFloat(ruleData.includedDistanceKm) || 0,
      additionalKmFee: parseFloat(ruleData.additionalKmFee) || 0,
      minimumFee: parseFloat(ruleData.minimumFee) || 0,
      maximumFee: parseFloat(ruleData.maximumFee) || 0,
      isActive: ruleData.isActive !== undefined ? ruleData.isActive : true,
      rowVersion: ruleData.rowVersion,
    };
    const response = await apiClient.post('/api/v1/restaurants/pricing-rules', payload);
    return response.data;
  },

  updatePricingRule: async (id, ruleData) => {
    const payload = {
      restaurantPricingPlanId: Number(ruleData.restaurantPricingPlanId),
      restaurantBranchId: ruleData.restaurantBranchId ? Number(ruleData.restaurantBranchId) : null,
      serviceZoneId: ruleData.serviceZoneId ? Number(ruleData.serviceZoneId) : null,
      calculationType: Number(ruleData.calculationType) || 1,
      fixedFee: parseFloat(ruleData.fixedFee) || 0,
      baseFee: parseFloat(ruleData.baseFee) || 0,
      includedDistanceKm: parseFloat(ruleData.includedDistanceKm) || 0,
      additionalKmFee: parseFloat(ruleData.additionalKmFee) || 0,
      minimumFee: parseFloat(ruleData.minimumFee) || 0,
      maximumFee: parseFloat(ruleData.maximumFee) || 0,
      isActive: ruleData.isActive !== undefined ? ruleData.isActive : true,
      rowVersion: ruleData.rowVersion,
    };
    const response = await apiClient.put(`/api/v1/restaurants/pricing-rules/${id}`, payload);
    return response.data;
  },

  // Pricing Assignments (Branch to Plan)
  getPricingAssignments: async (branchId) => {
    const url = branchId ? `/api/v1/restaurants/pricing-assignments?branchId=${branchId}` : '/api/v1/restaurants/pricing-assignments';
    const response = await apiClient.get(url);
    return response.data;
  },

  createPricingAssignment: async (data) => {
    const payload = {
      restaurantBranchId: Number(data.restaurantBranchId),
      restaurantPricingPlanId: Number(data.restaurantPricingPlanId),
      effectiveFrom: data.effectiveFrom || new Date().toISOString().split('T')[0],
      effectiveTo: data.effectiveTo || new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      isActive: data.isActive !== undefined ? data.isActive : true,
      rowVersion: data.rowVersion,
    };
    const response = await apiClient.post('/api/v1/restaurants/pricing-assignments', payload);
    return response.data;
  },

  updatePricingAssignment: async (id, data) => {
    const payload = {
      restaurantBranchId: Number(data.restaurantBranchId),
      restaurantPricingPlanId: Number(data.restaurantPricingPlanId),
      effectiveFrom: data.effectiveFrom,
      effectiveTo: data.effectiveTo,
      isActive: data.isActive !== undefined ? data.isActive : true,
      rowVersion: data.rowVersion,
    };
    const response = await apiClient.put(`/api/v1/restaurants/pricing-assignments/${id}`, payload);
    return response.data;
  },
};

// ==========================================
// 5.1 SERVICE ZONES (GEO) SERVICES
// ==========================================
// Helper to generate a valid WKT Polygon bounding box around center coordinates
const generateDefaultPolygonWkt = (lat = 30.0444, lng = 31.2357, radiusKm = 5) => {
  const validLat = parseFloat(lat) || 30.0444;
  const validLng = parseFloat(lng) || 31.2357;
  const latDelta = radiusKm / 111.0;
  const lngDelta = radiusKm / (111.0 * Math.cos((validLat * Math.PI) / 180.0) || 1.0);

  const minLat = (validLat - latDelta).toFixed(6);
  const maxLat = (validLat + latDelta).toFixed(6);
  const minLng = (validLng - lngDelta).toFixed(6);
  const maxLng = (validLng + lngDelta).toFixed(6);

  return `POLYGON ((${minLng} ${minLat}, ${maxLng} ${minLat}, ${maxLng} ${maxLat}, ${minLng} ${maxLat}, ${minLng} ${minLat}))`;
};

export const serviceZonesService = {
  getServiceZones: async (params = {}) => {
    const response = await apiClient.get('/api/v1/service-zones', { params });
    return response.data;
  },

  getServiceZoneById: async (id) => {
    const response = await apiClient.get(`/api/v1/service-zones/${id}`);
    return response.data;
  },

  createServiceZone: async (zoneData) => {
    const centerLat = parseFloat(zoneData.centerLatitude) || 30.0444;
    const centerLng = parseFloat(zoneData.centerLongitude) || 31.2357;
    const wkt = (zoneData.boundaryWkt && zoneData.boundaryWkt.trim())
      ? zoneData.boundaryWkt.trim()
      : generateDefaultPolygonWkt(centerLat, centerLng);

    const payload = {
      zoneCode: zoneData.zoneCode,
      zoneNameAr: zoneData.zoneNameAr,
      zoneNameEn: zoneData.zoneNameEn || zoneData.zoneNameAr,
      boundaryWkt: wkt,
      centerLatitude: centerLat,
      centerLongitude: centerLng,
      priority: Number(zoneData.priority) || 1,
      isActive: zoneData.isActive !== undefined ? zoneData.isActive : true,
      rowVersion: zoneData.rowVersion,
    };
    const response = await apiClient.post('/api/v1/service-zones', payload);
    return response.data;
  },

  updateServiceZone: async (id, zoneData) => {
    const centerLat = parseFloat(zoneData.centerLatitude) || 30.0444;
    const centerLng = parseFloat(zoneData.centerLongitude) || 31.2357;
    const wkt = (zoneData.boundaryWkt && zoneData.boundaryWkt.trim())
      ? zoneData.boundaryWkt.trim()
      : generateDefaultPolygonWkt(centerLat, centerLng);

    const payload = {
      zoneCode: zoneData.zoneCode,
      zoneNameAr: zoneData.zoneNameAr,
      zoneNameEn: zoneData.zoneNameEn || zoneData.zoneNameAr,
      boundaryWkt: wkt,
      centerLatitude: centerLat,
      centerLongitude: centerLng,
      priority: Number(zoneData.priority) || 1,
      isActive: zoneData.isActive !== undefined ? zoneData.isActive : true,
      rowVersion: zoneData.rowVersion,
    };
    const response = await apiClient.put(`/api/v1/service-zones/${id}`, payload);
    return response.data;
  },
};

// ==========================================
// 5.2 COURIER COMPENSATION & COMMISSION SERVICES
// ==========================================
export const compensationService = {
  // Compensation Plans
  getPlans: async () => {
    const response = await apiClient.get('/api/v1/compensation/plans');
    return response.data;
  },

  createPlan: async (planData) => {
    const payload = {
      planCode: planData.planCode,
      planNameAr: planData.planNameAr,
      compensationMode: Number(planData.compensationMode) || 1,
      hourlyRate: parseFloat(planData.hourlyRate) || 0,
      hourlyRoundingMinutes: Number(planData.hourlyRoundingMinutes) || 0,
      dailyRate: parseFloat(planData.dailyRate) || 0,
      minimumDailyEligibleMinutes: Number(planData.minimumDailyEligibleMinutes) || 0,
      minimumDailyCompletedOrders: Number(planData.minimumDailyCompletedOrders) || 0,
      dailyEligibilityMode: Number(planData.dailyEligibilityMode) || 1,
      allowProratedDailyRate: Boolean(planData.allowProratedDailyRate),
      additionalOrderPercentage: parseFloat(planData.additionalOrderPercentage) || 0,
      effectiveFrom: planData.effectiveFrom || new Date().toISOString().split('T')[0],
      effectiveTo: planData.effectiveTo || new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      isActive: planData.isActive !== undefined ? Boolean(planData.isActive) : true,
      rowVersion: planData.rowVersion,
    };
    const response = await apiClient.post('/api/v1/compensation/plans', payload);
    return response.data;
  },

  updatePlan: async (id, planData) => {
    const payload = {
      planCode: planData.planCode,
      planNameAr: planData.planNameAr,
      compensationMode: Number(planData.compensationMode) || 1,
      hourlyRate: parseFloat(planData.hourlyRate) || 0,
      hourlyRoundingMinutes: Number(planData.hourlyRoundingMinutes) || 0,
      dailyRate: parseFloat(planData.dailyRate) || 0,
      minimumDailyEligibleMinutes: Number(planData.minimumDailyEligibleMinutes) || 0,
      minimumDailyCompletedOrders: Number(planData.minimumDailyCompletedOrders) || 0,
      dailyEligibilityMode: Number(planData.dailyEligibilityMode) || 1,
      allowProratedDailyRate: Boolean(planData.allowProratedDailyRate),
      additionalOrderPercentage: parseFloat(planData.additionalOrderPercentage) || 0,
      effectiveFrom: planData.effectiveFrom,
      effectiveTo: planData.effectiveTo,
      isActive: planData.isActive !== undefined ? Boolean(planData.isActive) : true,
      rowVersion: planData.rowVersion,
    };
    const response = await apiClient.put(`/api/v1/compensation/plans/${id}`, payload);
    return response.data;
  },

  // Commission Rules
  getCommissionRules: async (planId) => {
    const url = planId ? `/api/v1/compensation/commission-rules?planId=${planId}` : '/api/v1/compensation/commission-rules';
    const response = await apiClient.get(url);
    return response.data;
  },

  createCommissionRule: async (ruleData) => {
    const payload = {
      courierCompensationPlanId: Number(ruleData.courierCompensationPlanId),
      pickupRestaurantBranchId: ruleData.pickupRestaurantBranchId ? Number(ruleData.pickupRestaurantBranchId) : null,
      destinationServiceZoneId: ruleData.destinationServiceZoneId ? Number(ruleData.destinationServiceZoneId) : null,
      calculationType: Number(ruleData.calculationType) || 1,
      fixedAmount: parseFloat(ruleData.fixedAmount) || 0,
      baseAmount: parseFloat(ruleData.baseAmount) || 0,
      includedDistanceKm: parseFloat(ruleData.includedDistanceKm) || 0,
      additionalKmAmount: parseFloat(ruleData.additionalKmAmount) || 0,
      minimumAmount: parseFloat(ruleData.minimumAmount) || 0,
      maximumAmount: parseFloat(ruleData.maximumAmount) || 0,
      isActive: ruleData.isActive !== undefined ? ruleData.isActive : true,
      rowVersion: ruleData.rowVersion,
    };
    const response = await apiClient.post('/api/v1/compensation/commission-rules', payload);
    return response.data;
  },

  updateCommissionRule: async (id, ruleData) => {
    const payload = {
      courierCompensationPlanId: Number(ruleData.courierCompensationPlanId),
      pickupRestaurantBranchId: ruleData.pickupRestaurantBranchId ? Number(ruleData.pickupRestaurantBranchId) : null,
      destinationServiceZoneId: ruleData.destinationServiceZoneId ? Number(ruleData.destinationServiceZoneId) : null,
      calculationType: Number(ruleData.calculationType) || 1,
      fixedAmount: parseFloat(ruleData.fixedAmount) || 0,
      baseAmount: parseFloat(ruleData.baseAmount) || 0,
      includedDistanceKm: parseFloat(ruleData.includedDistanceKm) || 0,
      additionalKmAmount: parseFloat(ruleData.additionalKmAmount) || 0,
      minimumAmount: parseFloat(ruleData.minimumAmount) || 0,
      maximumAmount: parseFloat(ruleData.maximumAmount) || 0,
      isActive: ruleData.isActive !== undefined ? ruleData.isActive : true,
      rowVersion: ruleData.rowVersion,
    };
    const response = await apiClient.put(`/api/v1/compensation/commission-rules/${id}`, payload);
    return response.data;
  },

  // Courier Plan Assignments
  getAssignments: async (courierId) => {
    const url = courierId ? `/api/v1/compensation/assignments?courierId=${courierId}` : '/api/v1/compensation/assignments';
    const response = await apiClient.get(url);
    return response.data;
  },

  createAssignment: async (data) => {
    const payload = {
      courierId: Number(data.courierId),
      courierCompensationPlanId: Number(data.courierCompensationPlanId),
      effectiveFrom: data.effectiveFrom || new Date().toISOString().split('T')[0],
      effectiveTo: data.effectiveTo || new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      isActive: data.isActive !== undefined ? data.isActive : true,
      rowVersion: data.rowVersion,
    };
    const response = await apiClient.post('/api/v1/compensation/assignments', payload);
    return response.data;
  },

  updateAssignment: async (id, data) => {
    const payload = {
      courierId: Number(data.courierId),
      courierCompensationPlanId: Number(data.courierCompensationPlanId),
      effectiveFrom: data.effectiveFrom,
      effectiveTo: data.effectiveTo,
      isActive: data.isActive !== undefined ? data.isActive : true,
      rowVersion: data.rowVersion,
    };
    const response = await apiClient.put(`/api/v1/compensation/assignments/${id}`, payload);
    return response.data;
  },

  // Cancellation Reasons & Rules
  getCancellationReasons: async () => {
    const response = await apiClient.get('/api/v1/compensation/cancellation-reasons');
    return response.data;
  },

  createCancellationReason: async (data) => {
    const response = await apiClient.post('/api/v1/compensation/cancellation-reasons', data);
    return response.data;
  },

  updateCancellationReason: async (id, data) => {
    const response = await apiClient.put(`/api/v1/compensation/cancellation-reasons/${id}`, data);
    return response.data;
  },

  getCancellationRules: async () => {
    const response = await apiClient.get('/api/v1/compensation/cancellation-rules');
    return response.data;
  },

  createCancellationRule: async (data) => {
    const response = await apiClient.post('/api/v1/compensation/cancellation-rules', data);
    return response.data;
  },

  updateCancellationRule: async (id, data) => {
    const response = await apiClient.put(`/api/v1/compensation/cancellation-rules/${id}`, data);
    return response.data;
  },
};

// ==========================================
// 5.3 DASHBOARD SERVICES (25 AUGUST 2026 SPEC)
// ==========================================
export const dashboardService = {
  getSummary: async (from, to) => {
    const params = {};
    if (from) params.from = from;
    if (to) params.to = to;
    const response = await apiClient.get('/api/v1/dashboard/summary', { params });
    return response.data;
  },

  getOrderFlow: async (from, to) => {
    const params = {};
    if (from) params.from = from;
    if (to) params.to = to;
    const response = await apiClient.get('/api/v1/dashboard/order-flow', { params });
    return response.data;
  },

  getExceptions: async (from, to, limit = 50) => {
    const params = { limit };
    if (from) params.from = from;
    if (to) params.to = to;
    const response = await apiClient.get('/api/v1/dashboard/exceptions', { params });
    return response.data;
  },

  getFinance: async (from, to) => {
    const params = {};
    if (from) params.from = from;
    if (to) params.to = to;
    const response = await apiClient.get('/api/v1/dashboard/finance', { params });
    return response.data;
  },
};

// ==========================================
// 6. FINANCE & SETTLEMENTS SERVICES
// ==========================================
export const financeService = {
  getSettlements: async (params = {}) => {
    const response = await apiClient.get('/api/v1/finance/settlements', { params });
    return response.data;
  },

  runSettlement: async (courierId, businessDate) => {
    const date = businessDate || new Date().toISOString().split('T')[0];
    const response = await apiClient.post(`/api/v1/couriers/${courierId}/earnings/${date}/settle`, {}, {
      headers: { 'Idempotency-Key': generateUUID() },
    });
    return response.data;
  },

  createSettlement: async (data) => {
    const rawPartyType = Number(data.partyType);
    // Backend enum: 2 = Restaurant, 3 = Courier (must be between 2 and 3)
    const partyType = (rawPartyType === 2 || rawPartyType === 3) ? rawPartyType : 3;

    const payload = {
      partyType: partyType,
      partyId: Number(data.partyId ?? data.courierId ?? 0),
      fromDate: data.fromDate || new Date().toISOString().split('T')[0],
      toDate: data.toDate || new Date().toISOString().split('T')[0],
      settlementAmount: parseFloat(data.settlementAmount ?? 0),
    };
    const response = await apiClient.post('/api/v1/finance/settlements', payload, {
      headers: { 'Idempotency-Key': generateUUID() },
    });
    return response.data;
  },

  getAdvances: async (params = {}) => {
    const response = await apiClient.get('/api/v1/finance/advances', { params });
    return response.data;
  },

  issueAdvance: async (courierId, amount, reason = '', advanceDate) => {
    const response = await apiClient.post('/api/v1/finance/advances', {
      courierId: Number(courierId),
      advanceDate: advanceDate || new Date().toISOString().split('T')[0],
      amount: parseFloat(amount),
      reason: reason || '',
    }, {
      headers: { 'Idempotency-Key': generateUUID() },
    });
    return response.data;
  },

  createAdvance: async (advanceData, amountArg, reasonArg, dateArg) => {
    let payload;
    if (typeof advanceData === 'object' && advanceData !== null) {
      payload = {
        courierId: Number(advanceData.courierId),
        advanceDate: advanceData.advanceDate || new Date().toISOString().split('T')[0],
        amount: parseFloat(advanceData.amount),
        reason: advanceData.reason || advanceData.notes || '',
      };
    } else {
      payload = {
        courierId: Number(advanceData),
        advanceDate: dateArg || new Date().toISOString().split('T')[0],
        amount: parseFloat(amountArg),
        reason: reasonArg || '',
      };
    }
    const response = await apiClient.post('/api/v1/finance/advances', payload, {
      headers: { 'Idempotency-Key': generateUUID() },
    });
    return response.data;
  },

  getStatement: async (params = {}) => {
    const response = await apiClient.get('/api/v1/reports/company', { params });
    return response.data;
  },

  getProfitability: async (params = {}) => {
    const response = await apiClient.get('/api/v1/reports/profitability', { params });
    return response.data;
  },

  exportCsv: async (reportType = 'statement', params = {}) => {
    const response = await apiClient.get('/api/v1/reports/company.csv', {
      params,
      responseType: 'blob',
    });
    return response.data;
  },
};

// ==========================================
// 7. REPORTS SERVICES
// ==========================================
export const reportsService = {
  // 1. Company Reports
  getCompanyReport: async (params = {}) => {
    const response = await apiClient.get('/api/v1/reports/company', { params });
    return response.data;
  },

  exportCompanyReportCsv: async (params = {}) => {
    const response = await apiClient.get('/api/v1/reports/company.csv', {
      params,
      responseType: 'blob',
    });
    return response.data;
  },

  // 2. Profitability Reports
  getProfitability: async (params = {}) => {
    const response = await apiClient.get('/api/v1/reports/profitability', { params });
    return response.data;
  },

  exportProfitabilityCsv: async (params = {}) => {
    const response = await apiClient.get('/api/v1/reports/profitability.csv', {
      params,
      responseType: 'blob',
    });
    return response.data;
  },

  // 3. Restaurant Statements
  getRestaurantStatement: async (restaurantId, params = {}) => {
    if (!restaurantId) throw new Error('Restaurant ID is required');
    const response = await apiClient.get(`/api/v1/reports/restaurants/${restaurantId}/statement`, { params });
    return response.data;
  },

  exportRestaurantStatementCsv: async (restaurantId, params = {}) => {
    if (!restaurantId) throw new Error('Restaurant ID is required');
    const response = await apiClient.get(`/api/v1/reports/restaurants/${restaurantId}/statement.csv`, {
      params,
      responseType: 'blob',
    });
    return response.data;
  },

  // 4. Courier Statements
  getCourierStatement: async (courierId, params = {}) => {
    if (!courierId) throw new Error('Courier ID is required');
    const response = await apiClient.get(`/api/v1/reports/couriers/${courierId}/statement`, { params });
    return response.data;
  },

  exportCourierStatementCsv: async (courierId, params = {}) => {
    if (!courierId) throw new Error('Courier ID is required');
    const response = await apiClient.get(`/api/v1/reports/couriers/${courierId}/statement.csv`, {
      params,
      responseType: 'blob',
    });
    return response.data;
  },
};

